import { merge } from 'lodash-es';

import { primitive } from 'styles/constants/primitives';

import { sharedPrimitives } from '../shared';

const colorTokens = {
  'background-active': primitive.bk.$fieryRed,
  'background-badge': primitive.bk.$bbqBrown,
  'background-badge-rewards': primitive.bk.$bbqBrown,
  'background-badge-rewards-locked': primitive.bk.$mayoEggWhite,
  'background-button-primary-default': primitive.bk.$fieryRed,
  'background-button-primary-hover': primitive.bk.$fieryRedHover,
  'background-button-primary-pressed': primitive.bk.$fieryRedPressed,
  'background-button-secondary-hover': primitive.bk.$bbqBrownOpacity15,
  'background-button-secondary-pressed': primitive.bk.$bbqBrownOpacity20,
  'background-checked': primitive.bk.$bbqBrown,
  'background-default': primitive.bk.$mayoEggWhite,
  'background-footer': primitive.bk.$bbqBrown,
  'background-hero-light': primitive.bk.$mayoEggWhite,
  'background-hero-reversed': primitive.bk.$bbqBrown,
  'background-loader-indicator-primary': primitive.bk.$fieryRed,
  'background-skeleton-loader-cta-start': '#D623004D',
  'background-skeleton-loader-cta-end': '#D623001A',
  'background-switch-track': primitive.bk.$bbqBrown,
  'border-color-active': primitive.bk.$fieryRed,
  'border-color-badge': primitive.bk.$bbqBrown,
  'border-color-button-secondary': primitive.bk.$bbqBrown,
  'border-color-checked': primitive.bk.$bbqBrown,
  'border-color-focus': primitive.bk.$bbqBrown,
  'border-color-hover': primitive.bk.$bbqBrown,
  'border-color-offer-applied': '#FF8732',
  'icon-active': primitive.bk.$fieryRed,
  'icon-button-primary-reversed': primitive.bk.$bbqBrown,
  'icon-button-secondary': primitive.bk.$bbqBrown,
  'icon-button-tertiary-default': primitive.bk.$bbqBrown,
  'icon-button-tertiary-hover': primitive.bk.$fieryRed,
  'icon-button-tertiary-pressed': primitive.bk.$fieryRed,
  'icon-default': primitive.bk.$bbqBrown,
  'icon-form-input-action': primitive.bk.$bbqBrown,
  'icon-reversed': primitive.bk.$mayoEggWhite,
  'icon-rewards': primitive.bk.$bbqBrown,
  'icon-rewards-locked': '#FFAA00',
  'text-active': primitive.bk.$fieryRed,
  'text-button-primary-reversed': primitive.bk.$bbqBrown,
  'text-button-secondary': primitive.bk.$bbqBrown,
  'text-button-tertiary-default': primitive.bk.$bbqBrown,
  'text-button-tertiary-hover': primitive.bk.$fieryRed,
  'text-default': primitive.bk.$bbqBrown,
  'text-hero-light': primitive.bk.$bbqBrown,
  'text-link-default': primitive.bk.$bbqBrown,
  'text-link-hover': primitive.bk.$fieryRed,
  'text-link-reversed': primitive.bk.$mayoEggWhite,
  'text-reversed': primitive.bk.$mayoEggWhite,
  'text-rewards': primitive.bk.$bbqBrown,
  'text-rewards-locked': primitive.bk.$mayoEggWhite,
};

// @todo update with color from Design
// http://mcg.mbitson.com/#!?plk=%23f25600&bk=%23502314&th=%23c8102e&themename=mcgtheme
// Based on bbqBrown
const primary = {
  50: '#eae5e3',
  100: '#cbbdb9',
  200: '#a8918a',
  300: '#85655b',
  400: '#6a4437',
  500: primitive.bk.$bbqBrown,
  600: '#491f12',
  700: '#401a0e',
  800: '#37150b',
  900: '#270c06',
};

// Based on fieryRed
const secondary = {
  50: '#fae5e0',
  100: '#f3bdb3',
  200: '#eb9180',
  300: '#e2654d',
  400: '#dc4426',
  500: primitive.bk.$fieryRed,
  600: '#d11f00',
  700: '#cc1a00',
  800: '#c61500',
  900: '#bc0c00',
};

// Based on crunchyGreen
const tertiary = {
  50: '#e3f1e7',
  100: '#badbc3',
  200: '#8cc39b',
  300: '#5eab73',
  400: '#3c9955',
  500: '#198737',
  600: '#167f31',
  700: '#12742a',
  800: '#0e6a23',
  900: '#085716',
};
// /@todo

export const bkPrimitives = merge({}, sharedPrimitives, {
  colors: {
    primary,
    secondary,
    tertiary,
    bbqBrown: primitive.bk.$bbqBrown,
    mayoEggWhite: primitive.bk.$mayoEggWhite,
    fieryRed: primitive.bk.$fieryRed,
    crunchyGreen: 'rgb(25,135,55)',
    flamingOrange: 'rgb(255,135,50)',
    meltyYellow: 'rgb(255,170,0)',
    fieryRedOpacity10: primitive.bk.$fieryRedOpacity10,
    fieryRedDarken10: primitive.bk.$fieryRedDarken10,
    token: colorTokens,
  },
  fontConfig: {
    Flame: {
      400: {
        normal: 'Flame-Regular',
        // There is no italic version of this font
        // Setting to the same as normal so that we don't break iOS
        italic: 'Flame-Regular',
      },
      700: {
        normal: 'Flame-Bold',
        // There is no italic version of this font
        // Setting to the same as normal so that we don't break iOS
        italic: 'Flame-Bold',
      },
    },
    'Flame-Bold': {
      // Force bold font regardless of what font-weight is set
      400: {
        normal: 'Flame-Bold',
        // There is no italic version of this font
        // Setting to the same as normal so that we don't break iOS
        italic: 'Flame-Bold',
      },
      700: {
        normal: 'Flame-Bold',
        // There is no italic version of this font
        // Setting to the same as normal so that we don't break iOS
        italic: 'Flame-Bold',
      },
    },
    'Flame-Sans': {
      400: {
        normal: 'Flame-RegularSans',
        // There is no italic version of this font
        // Setting to the same as normal so that we don't break iOS
        italic: 'Flame-RegularSans',
      },
      // We might need to change this in the future, but design has said
      // 1) There is no existing Flame-BoldSans font file
      // 2) They sometimes use Flame-Regular as the bold variant
      700: {
        normal: 'Flame-Regular',
        italic: 'Flame-Regular',
      },
    },
  },
  fonts: {
    heading: 'Flame',
    body: 'Flame-Sans',
    alternative: 'Flame-Bold',
  },
});
